import { ChangeDetectionStrategy, Component } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { TestResultStatus } from '@y42/data-test';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTestResultToIcon } from '../../../state';
import { DataTestJobOverviewListUIModel } from '../../data-test-job-overview/data-test-job-overview-list.model';

interface StatusCellRendererAggData {
  type: TestResultStatus;
  count: number;
}

interface ParamsValue {
  value: TestResultStatus | StatusCellRendererAggData[] | null;
  errors: string[];
}

@UntilDestroy()
@Component({
  selector: 'y42-data-test-result-cell-renderer',
  templateUrl: './data-test-result-cell-renderer.component.html',
  styleUrls: ['./data-test-result-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTestResultCellRendererComponent implements ICellRendererAngularComp {
  private readonly params$$ = new BehaviorSubject<ParamsValue>({ value: null, errors: [] });
  private readonly leafNodeValue$ = this.params$$.pipe(
    map((params) => (this.isTestResultStatusEnum(params.value) ? params.value : null)),
  );
  readonly state = connectState(this, {
    aggregationGroupArray: this.params$$.pipe(
      map((params) => (this.isStatusCellRendererAggDataArray(params.value) ? params.value : null)),
    ),
    leafNodeValue: this.leafNodeValue$,
    errorMessages: this.params$$.pipe(map((params) => params.errors)),
  });

  readonly trackByFn = getTrackByFn<StatusCellRendererAggData>('type');

  agInit(
    params: ICellRendererParams<DataTestJobOverviewListUIModel, TestResultStatus | StatusCellRendererAggData[]>,
  ): void {
    this.refreshData(params);
  }

  refresh(
    params: ICellRendererParams<DataTestJobOverviewListUIModel, TestResultStatus | StatusCellRendererAggData[]>,
  ): boolean {
    this.refreshData(params);
    return true;
  }

  private isTestResultStatusEnum(obj: unknown): obj is TestResultStatus {
    return typeof obj === 'string' && obj in DataTestResultToIcon;
  }

  private isStatusCellRendererAggDataArray(obj: unknown): obj is StatusCellRendererAggData[] {
    return Array.isArray(obj);
  }

  private refreshData(
    params: ICellRendererParams<DataTestJobOverviewListUIModel, TestResultStatus | StatusCellRendererAggData[]>,
  ) {
    this.params$$.next({
      value: params.value,
      errors: params.data?.test?.result?.error_messages ?? [],
    });
  }
}
